'use client'

import { ReactNode } from 'react'
import { Cell, Text } from '@vinted/web-ui'
import { compact, mapKeys } from 'lodash'

import { AddressModel } from '@marketplace-web/address/address-data'
import { BusinessRegistrationAddressModel } from '@marketplace-web/business-accounts/business-accounts-data'

const MAX_CHARACTERS = 38

const toDataAttributes = (dataSet: object = {}) =>
  mapKeys(dataSet, (_value, key) => `data-${key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()}`)

const truncateString = (text: string, length: number) =>
  text.length > length ? `${text.substring(0, length)}...` : text

type Props = {
  styling?: React.ComponentProps<typeof Cell>['styling']
  type?: React.ComponentProps<typeof Cell>['type']
  title?: string
  address: AddressModel | BusinessRegistrationAddressModel | null | undefined
  onClick?: () => void
  showChevron?: boolean
  url?: string
  data?: object
  suffix?: ReactNode
  isDisabled?: boolean
  validation?: ReactNode
  prefix?: ReactNode
}

const AddressDetails = ({
  styling,
  type,
  title,
  onClick,
  suffix,
  prefix,
  url,
  data,
  validation,
  address,
  showChevron = false,
  isDisabled = false,
}: Props) => {
  const renderBody = () => {
    if (!address) return null

    const { line1, line2, postalCode, city } = address

    return (
      <>
        {title && address.name && <Text as="span" text={address.name} width={Text.Width.Parent} />}
        {!!(line1 || line2) && (
          <Text
            as="span"
            text={compact([address.line1, address.line2]).join(', ')}
            width={Text.Width.Parent}
          />
        )}
        {!!(postalCode || city) && (
          <Text as="span" text={compact([address.postalCode, address.city]).join(', ')} />
        )}
      </>
    )
  }

  if (!address) return null

  return (
    <Cell
      styling={styling}
      type={type}
      chevron={showChevron}
      title={
        (title && truncateString(title, MAX_CHARACTERS)) ||
        (address.name && truncateString(address.name, MAX_CHARACTERS))
      }
      body={renderBody()}
      suffix={suffix}
      onClick={onClick}
      url={url}
      urlProps={{ ...toDataAttributes(data) }}
      disabled={isDisabled}
      validation={validation}
      prefix={prefix}
      testId="address-details"
    />
  )
}

export default AddressDetails
